import { type ReactElement, useId } from 'react';

import { JOB_ROLES } from '@/shared/config';

import {
  composeNickname,
  defaultTagFor,
  NICKNAME_HANDLE_MAX,
  NICKNAME_SEPARATOR,
  NICKNAME_TAG_MAX,
  type NicknameValue,
  validateNicknameHandle,
  validateNicknameTag,
} from '../lib/nickname';

const CUSTOM_TAG_ROLE = '기타';

const labelClass = 'text-[12.5px] font-medium text-muted-foreground';

const controlClass =
  'h-11 rounded-md border border-border bg-card px-3 text-sm ' +
  'focus:border-foreground focus:outline-none focus:ring-[3px] focus:ring-primary';

/**
 * 구조화 닉네임 입력 — `[직무 ▾] # [자유입력]`.
 *
 * **합쳐진 값만 보여준다.** 태그와 이름을 따로 입력받으면서 결과를 안 보여주면 자기 닉네임이
 * 어떻게 보일지 모르는 채로 넘어간다. 문장으로 설명하지 않고 값 하나만 띄운다.
 *
 * 에러는 **입력을 시작한 뒤에만** 띄운다. 빈 칸에 처음부터 빨간 글씨가 떠 있으면 폼이
 * 사람을 나무라는 것으로 읽힌다 — 잠긴 제출 버튼이 이미 「아직 덜 됐다」를 말한다.
 */
export function NicknameField({
  value,
  onChange,
  idPrefix,
}: {
  value: NicknameValue;
  onChange: (next: NicknameValue) => void;
  idPrefix?: string;
}): ReactElement {
  const generatedId = useId();
  const prefix = idPrefix ?? generatedId;
  const roleId = `${prefix}-role`;
  const tagId = `${prefix}-tag`;
  const handleId = `${prefix}-handle`;

  const isCustomTag = value.jobRole === CUSTOM_TAG_ROLE;
  const handleError = value.handle ? validateNicknameHandle(value.handle) : null;
  const tagError = isCustomTag && value.jobRoleTag ? validateNicknameTag(value.jobRoleTag) : null;
  const preview = composeNickname(value.jobRoleTag, value.handle);

  // 직무를 바꾸면 태그도 같이 간다 — 「기타」로 옮길 때는 비워서 직접 적게 한다
  const selectRole = (jobRole: string) =>
    onChange({ ...value, jobRole, jobRoleTag: defaultTagFor(jobRole) });

  return (
    <fieldset className="flex flex-col gap-1.5">
      <legend className={labelClass}>닉네임</legend>

      <div className="flex items-center gap-2">
        {isCustomTag ? (
          <input
            id={tagId}
            type="text"
            aria-label="직무 태그"
            maxLength={NICKNAME_TAG_MAX}
            placeholder="물류"
            value={value.jobRoleTag}
            onChange={(event) => onChange({ ...value, jobRoleTag: event.target.value })}
            className={`${controlClass} w-[7.5rem] shrink-0`}
          />
        ) : (
          <select
            id={roleId}
            aria-label="직무 태그"
            value={value.jobRole}
            onChange={(event) => selectRole(event.target.value)}
            className={`${controlClass} w-[7.5rem] shrink-0`}
          >
            <option value="">직무 선택</option>
            {JOB_ROLES.map((role) => (
              <option key={role} value={role}>
                {defaultTagFor(role) || '직접 입력'}
              </option>
            ))}
          </select>
        )}

        <span aria-hidden="true" className="text-sm text-muted-foreground">
          {NICKNAME_SEPARATOR}
        </span>

        <input
          id={handleId}
          type="text"
          aria-label="닉네임"
          maxLength={NICKNAME_HANDLE_MAX}
          placeholder="민지"
          value={value.handle}
          onChange={(event) => onChange({ ...value, handle: event.target.value })}
          className={`${controlClass} min-w-0 flex-1`}
        />
      </div>

      {isCustomTag ? (
        <button
          type="button"
          onClick={() => selectRole('')}
          className="w-fit text-[12px] text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          목록에서 고르기
        </button>
      ) : null}

      {(tagError ?? handleError) ? (
        <span className="text-[12px] text-destructive">{tagError ?? handleError}</span>
      ) : preview ? (
        <span className="text-[12px] font-semibold">{preview}</span>
      ) : null}
    </fieldset>
  );
}
